import { PageTitle } from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getOrganizationApplications } from '../../common/api/application';
import {
  GettingStartedInformation,
  ReadGettingStartedInformation,
  StateRequiredForDone,
} from '../../common/api/getting-started';
import grayLine from '../../common/assets/svg/gray-line-arrow.svg';
import { ProgressCard } from '../../common/components/ProgessCard';
import { AppStatus } from '../../common/enums';
import { setApplications } from '../../common/reducers/applicationSlice';

export const GettingStarted: React.FunctionComponent = () => {
  const { currentOrg } = useOrganization();
  const { currentTeam } = useTeam();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [builtInApis, setBuiltInApis] =
    React.useState<GettingStartedInformation[]>();
  const orgApplications: Application[] = useSelector<AppState, Application[]>(
    ({ applications }) => {
      return applications;
    }
  );

  React.useEffect(() => {
    if (currentOrg && currentTeam) {
      const getOrgApps = async () => {
        const orgApps = await getOrganizationApplications(
          currentOrg.id,
          currentTeam.id
        );

        if (orgApps) {
          dispatch(setApplications(orgApps));
        }
      };
      getOrgApps();
    }
  }, [currentOrg, currentTeam]);

  React.useEffect(() => {
    const getApiInformation = async () => {
      const apiInformation = await ReadGettingStartedInformation();

      if (apiInformation) {
        setBuiltInApis(apiInformation);
      }
    };
    getApiInformation();
  }, []);

  const isStateStepComplete = (requireState: StateRequiredForDone): boolean => {
    switch (requireState) {
      case StateRequiredForDone.Organization:
        if (currentOrg) {
          return true;
        }
        break;
      case StateRequiredForDone.Team:
        if (currentTeam) {
          return true;
        }
        break;
      case StateRequiredForDone.Application:
        if (orgApplications && orgApplications.length > 0) {
          return true;
        }
        break;
      case StateRequiredForDone.Published:
        if (
          orgApplications &&
          orgApplications.find((app) => app.status != AppStatus.DEVELOPMENT)
        ) {
          return true;
        }
        break;
      default:
        return false;
    }
    return false;
  };

  const isCurrentStateStep = (requiredState: StateRequiredForDone): boolean => {
    switch (requiredState) {
      case StateRequiredForDone.Organization:
        if (currentOrg) {
          return false;
        }
        return true;
      case StateRequiredForDone.Team:
        if (!currentOrg) {
          return false;
        }
        if (currentOrg && currentTeam) {
          return false;
        }
        return true;
      case StateRequiredForDone.Application:
        if (!currentOrg || !currentTeam) {
          return false;
        }
        if (
          currentOrg &&
          currentTeam &&
          orgApplications &&
          orgApplications.length > 0
        ) {
          return false;
        }
        return true;
      case StateRequiredForDone.Published:
        if (
          currentOrg &&
          currentTeam &&
          orgApplications &&
          orgApplications.length > 0
        ) {
          return true;
        }
        return false;
      default:
        return false;
    }
  };

  const getStateRedirectFunction = (requiredState: StateRequiredForDone) => {
    switch (requiredState) {
      case StateRequiredForDone.Organization:
        return onSetupOrganizationClicked;
      case StateRequiredForDone.Team:
        return onJoinATeam;
      case StateRequiredForDone.Application:
        return onRegisterAnAppClicked;
      case StateRequiredForDone.Published:
        return onSubmitApp;
    }
  };

  const getStateActionText = (requiredState: StateRequiredForDone): string => {
    switch (requiredState) {
      case StateRequiredForDone.Organization:
        return 'Setup Org';
      case StateRequiredForDone.Team:
        return 'Setup Team';
      case StateRequiredForDone.Application:
        return 'Setup App';
      case StateRequiredForDone.Published:
        return 'Submit App';
    }
  };

  const onSetupOrganizationClicked = React.useCallback(
    () => navigate('/organization', { replace: true }),
    [navigate]
  );

  const onRegisterAnAppClicked = React.useCallback(
    () => navigate('/application', { replace: true }),
    [navigate]
  );

  const onJoinATeam = React.useCallback(
    () => navigate('/team', { replace: true }),
    [navigate]
  );

  const onSubmitApp = React.useCallback(
    () => navigate('/application/check', { replace: true }),
    [navigate]
  );

  return (
    <div className="flex flex-col p-os1-spacing">
      <div
        className="flex flex-row w-full mb-5"
        data-testid="getting-started-title"
      >
        <PageTitle>Getting started with OS1</PageTitle>
      </div>
      <div className="flex flex-row">
        {builtInApis?.map(
          ({ icon, title, description, stateRequiredForDone }, index) => {
            return (
              <React.Fragment key={`${index}_${title}`}>
                <ProgressCard
                  action={getStateRedirectFunction(stateRequiredForDone)}
                  actionText={getStateActionText(stateRequiredForDone)}
                  description={description}
                  icon={icon}
                  isActionActive={isCurrentStateStep(stateRequiredForDone)}
                  isActionCompleted={isStateStepComplete(stateRequiredForDone)}
                  title={title}
                />
                {index != builtInApis?.length - 1 ? (
                  <div className="flex flex-col justify-center">
                    <img alt="" src={grayLine} />
                  </div>
                ) : null}
              </React.Fragment>
            );
          }
        )}
      </div>
    </div>
  );
};
